import { useEffect, useState } from "react";
import axios from "axios";
import { API, assetUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { MapPin, CalendarBlank, Camera, PlayCircle, SpinnerGap } from "@phosphor-icons/react";

// Reusable contextual media feed — deployable on any SocialPulse page.
// Props: entityType, entityId, site, place, tags, title, layout ('grid'|'carousel'), limit
export default function ContextFeed({ entityType, entityId, site, place, tags, title, layout = "grid", limit = 12, showEmpty = false }) {
  const [media, setMedia] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (entityType) params.set("entity_type", entityType);
    if (entityId) params.set("entity_id", entityId);
    if (site) params.set("site", site);
    if (place) params.set("place", place);
    if (tags) params.set("tags", tags);
    params.set("limit", limit);
    axios.get(`${API}/context/media?${params.toString()}`)
      .then((r) => setMedia(r.data.media)).catch(() => setMedia([]));
  }, [entityType, entityId, site, place, tags, limit]);

  if (media === null) return <div className="grid place-items-center py-10" data-testid="context-feed-loading"><SpinnerGap size={26} className="animate-spin text-[#F27A54]" /></div>;
  if (media.length === 0) return showEmpty ? <p className="py-8 text-center text-sm text-muted-foreground" data-testid="context-feed-empty">No photos available for this yet.</p> : null;

  return (
    <section data-testid="context-feed">
      {title && <h2 className="mb-3 font-display text-lg font-black tracking-tight">{title}</h2>}
      <div className={layout === "carousel" ? "flex gap-4 overflow-x-auto pb-3" : "grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4"}>
        {media.map((m) => {
          const a = m.attribution || {};
          return (
            <Link key={m.id} to={a.canonical_url || `/p/${m.id}`} data-testid={`context-card-${m.id}`}
              className={`group overflow-hidden rounded-xl border border-border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-md ${layout === "carousel" ? "w-56 flex-shrink-0" : ""}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                <img src={assetUrl(m.image_url)} alt={m.alt_text || m.title || "photo"} loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {m.media_type === "video" && <PlayCircle size={40} weight="fill" className="absolute inset-0 m-auto text-white/90 drop-shadow" />}
              </div>
              <div className="p-3">
                <p className="line-clamp-1 text-sm font-bold">{m.title || "Untitled"}</p>
                <div className="mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
                  {a.photographer && <p className="flex items-center gap-1"><Camera size={11} weight="fill" /> {a.photographer}</p>}
                  {a.location && <p className="flex items-center gap-1"><MapPin size={11} weight="fill" /> {a.location}</p>}
                  {a.date_taken && <p className="flex items-center gap-1"><CalendarBlank size={11} weight="fill" /> {String(a.date_taken).slice(0, 10)}</p>}
                </div>
                <p className="mt-1.5 text-[9px] font-semibold uppercase tracking-wide text-[#F27A54]">PhotoPulse.pro</p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
